import React, { useEffect, useState, useCallback, type ChangeEvent } from "react";

import { Constants } from "@eagerdog/constants";
import { IShow, IScore, AkcAgilityScoreDto } from "@eagerdog/interfaces";

import { apiService } from "src/services";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Input from "src/components/Input/Input";
import Checkbox from "src/components/Checkbox/Checkbox";
import { StopWatch, convertTimeToSeconds, secondsToTime } from "src/components/StopWatchInput";

import { calculateNewPlacement } from "../utils/getScorePlacement";

import "./AgilityResults.scss";

interface IProps {
  show?: IShow,
  result: IScore,
  onChange(result: AkcAgilityScoreDto): void
}

const qualifiedOptions: IOption[] = [
  { id: Constants.score_result_type.qualified, value: "Qualified" },
  { id: Constants.score_result_type.not_qualified, value: "Not qualified" },
];

const AkcAgilityResults: React.FC<IProps> = ({ show, result, onChange}) => {
  const _result = result.akc_agility;

  const getSctFromShowConfig = () => {
    const showConfig = show?.akc_agility_sct;

    if (!showConfig) {
      return "00:00";
    }

    const dogLevel = result.dog_class!.level;
    const jumpHeight = result.agility_jump_height?.toString();

    let configLevel = dogLevel;

    const isPreferred = dogLevel.includes("Preferred");
    if (isPreferred) {
      configLevel = dogLevel.replace(" Preferred", "");
    }

    const isPremier = dogLevel.includes("Premier");
    if (isPremier) {
      configLevel = "Premier";
    }

    if ([
      Constants.dog_class_element_level.novice_a,
      Constants.dog_class_element_level.novice_b
    ].includes(configLevel)) {
      configLevel = "Novice";
    }

    const config = showConfig[configLevel];

    const sctConfig = config.find((c) => {
      if (isPreferred) {
        return c.preferred_jump_height === jumpHeight;
      }
      if (jumpHeight === "20" || jumpHeight === "26") {
        return c.regular_jump_height === "20/24C";
      }

      return c.regular_jump_height === jumpHeight;
    });

    if (sctConfig) {
      const sct = isPreferred ? sctConfig.preferred_sct : sctConfig.regular_sct;
      if (sct) {
        return secondsToTime(sct);
      }

      return "00:00";
    }

    return "00:00";
  }

  const [scores, setScores] = useState<IScore[]>([]);

  const [time, setTime] = useState<string>(_result?.search_time ?? "");
  const [courseTime, setCourseTime] = useState<string>( _result?.course_time ?? getSctFromShowConfig() ?? "");

  const [refusals, setRefusals] = useState<number>(_result?.refusals ?? 0);
  const [tableFaults, setTableFaults] = useState<number>(_result?.table_faults ?? 0);
  const [wrongCourse, setWrongCourse] = useState<number>(_result?.wrong_course ?? 0);
  const [failure, setFailure] = useState<boolean>(_result?.failed ?? false);
  const [excusal, setExcusal] = useState<boolean>(_result?.excusal ?? false);

  const [feo, setFeo] = useState<boolean>(_result?.feo ?? false);
  const [absent, setAbsent] = useState<boolean>(_result?.absent ?? false);

  const [resultType, setResultType] = useState<IOption>({
    value: _result?.result_type === Constants.score_result_type.qualified ?  "Qualified" : "Not qualified",
    id: _result?.result_type === Constants.score_result_type.qualified ?  Constants.score_result_type.qualified : Constants.score_result_type.not_qualified,
  });

  const [comment, setComment] = useState<string>(_result?.comment ?? "");

  const [loaded, setLoaded] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const calculateOverTime = useCallback(() => {
    const dogTime = convertTimeToSeconds(time);
    const sct = convertTimeToSeconds(courseTime);

    if (!isNaN(dogTime) && !isNaN(sct)) {
      const overTimeSeconds = dogTime - sct;

      if (overTimeSeconds > 0) {
        return overTimeSeconds;
      }
    }

    return 0;
  }, [courseTime, time]);

  const calculateTimeFaults = useCallback(() => {
    const timePenaltyByLevel = () => {
      if (result.dog_class?.level.includes("Novice")) {
        return 1;
      }
      if (result.dog_class?.level.includes("Open")) {
        return 2;
      }
      if (["Excellent", "Master"].includes(result.dog_class?.level ?? "")) {
        return 3;
      }
      return 1;
    }

    return  timePenaltyByLevel() * calculateOverTime();
  }, [calculateOverTime, result.dog_class?.level]);

  const calculateTotalFaults = useCallback(() => {
    return calculateTimeFaults() + (refusals * 5) + (tableFaults * 5) + (wrongCourse * 5);
  }, [calculateTimeFaults, refusals, tableFaults, wrongCourse]);

  const calculateScore = useCallback(() => {
    return 100 - calculateTotalFaults();
  }, [calculateTotalFaults]);


  useEffect(() => {
    if (!loaded) {
      if (show) {
        apiService.getScoresGroupPlacements(
          show.event_id,
          result._id,
          {
            resultField: "akc_agility",
            stopWatchTimeField: "search_time",
            jumpHeightField: "agility_jump_height",
          }
        )
          .then((scores) => {
            setScores(scores);
          })
          .catch(() => {})
      }

      setLoaded(true);
    }
  }, [loaded, result._id, show]);

  useEffect(() => {
    const score = calculateScore();

    if (hasChanged) {
      onChange({
        result_type: resultType.id,
        score,
        search_time: time,
        course_time: courseTime,
        table_faults: tableFaults,
        wrong_course: wrongCourse,
        refusals,
        failed: failure,
        excusal,
        total_faults: calculateTotalFaults(),
        feo,
        absent,
        comment,
      });

      setHasChanged(false);
    }
  }, [hasChanged, calculateScore, calculateTotalFaults, time, courseTime, tableFaults, wrongCourse, refusals, failure, excusal, feo, absent, resultType.id, comment, onChange]);

  useEffect(() => {
    let overSct = false;
    const dogTime = convertTimeToSeconds(time);
    const sct = convertTimeToSeconds(courseTime);

    if (!isNaN(dogTime) && !isNaN(sct)) {
      const overTimeSeconds = dogTime - sct;
      overSct = overTimeSeconds >= 5;
    }

    if (failure || excusal || overSct) {
      setResultType({
        value: "Not qualified",
        id: Constants.score_result_type.not_qualified,
      })
    } else {
      setResultType({
        value: "Qualified",
        id: Constants.score_result_type.qualified,
      })
    }
  }, [failure, excusal, time, courseTime]);

  // set Q/NQ based on score
  // if ([
  //   Constants.dog_class_element.standard,
  //   Constants.dog_class_element.jumpers_with_weaves,
  //   Constants.dog_class_element.premier_standard,
  //   Constants.dog_class_element.premier_jww,
  // ].includes(result.dog_class?.show_element ?? "")) {
  //   if (score < 85) {
  //     setResultType({
  //       value: "Not qualified",
  //       id: Constants.score_result_type.not_qualified,
  //     })
  //   }
  // }

  const calculatePlacement = useCallback((): number | undefined => {
    return calculateNewPlacement({
      showType: "akc_agility",
      result,
      scores,
      calculateScore,
      time,
    });
  }, [result, calculateScore, time, scores]);

  const handleDogTimeChange = (value: string) => {
    setTime(value);
    setHasChanged(true);
  }

  const handleCourseTimeChange = (value: string) => {
    setCourseTime(value);
    setHasChanged(true);
  }

  const handleRefusalsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRefusals(Number(event.target.value));
    setHasChanged(true);
  }

  const handleTableFaultsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTableFaults(Number(event.target.value));
    setHasChanged(true);
  }

  const handleWrongCourseChange = (event: ChangeEvent<HTMLInputElement>) => {
    setWrongCourse(Number(event.target.value));
    setHasChanged(true);
  }

  const handleFailureChange = () => {
    setFailure(!failure);
    setHasChanged(true);
  }

  const handleExcusalChange = () => {
    setExcusal(!excusal);
    setHasChanged(true);
  }

  const handleFeoChange = () => {
    setFeo(!feo);
    setHasChanged(true);
  }

  const handleAbsentChange = () => {
    setAbsent(!absent);
    setHasChanged(true);
  }

  const handleResultTypeChange = (value: IOption) => {
    setResultType(value);
    setHasChanged(true);
  }

  const handleCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
    setHasChanged(true);
  }

  const dogClass = result.dog_class;

  return (
    <div className="AgilityResults">
      <StopWatch label="Time to Complete" time={time} onChange={handleDogTimeChange} />
      <StopWatch label="Standard Course Time" time={courseTime} enableMillisecondsInput={false} onChange={handleCourseTimeChange} />

      <Input type="number" min={0} onChange={handleRefusalsChange} label="R (Refusals)" placeholder="Enter the refusals count" defaultValue={refusals.toString()} />
      {[Constants.dog_class_element.premier_standard, Constants.dog_class_element.premier_jww].includes(result.dog_class?.show_element ?? "") &&
        <Input type="number" min={0} onChange={handleTableFaultsChange} label="T (Table Faults)" placeholder="Enter the table faults count" defaultValue={tableFaults.toString()} />
      }
      <Input type="number" min={0} onChange={handleWrongCourseChange} label="W (Wrong Course)" placeholder="Enter the wrong course count" defaultValue={wrongCourse.toString()} />
      <Checkbox id="failure-cb" label="F (Failure)" value={failure} onChange={handleFailureChange} />
      <Checkbox id="excusal-cb" label="E (Excusal)" value={excusal} onChange={handleExcusalChange} />

      <Input type="number" disabled label="Time Faults" placeholder="Time faults count" value={calculateTimeFaults()} />
      <Input type="number" disabled label="Seconds Over SCT" placeholder="Time over SCT in seconds" value={calculateOverTime()} />
      <Input type="number" disabled label="Total Faults" placeholder="Total faults count" value={calculateTotalFaults()} />

      <Checkbox id="feo-cb" label="FEO" value={feo} onChange={handleFeoChange} />
      <Checkbox id="absent-cb" label="Confirmed Absent" value={absent} onChange={handleAbsentChange} />

      <Dropdown value={resultType} label="Qualified?" onChange={(_e, value: IOption) => { handleResultTypeChange(value); }} options={qualifiedOptions} placeholder="" />

      <Input type="number" disabled  label="Score" placeholder="Score (maximum is 100)" value={calculateScore()} />

      <p>{`Result in - Class: ${dogClass?.show_element ?? ""}, Course: ${dogClass?.level} ${dogClass?.level.includes("Preferred") ? "" : "Standard"}, Jump height: ${result.agility_jump_height}"`}</p>
      <Input type="number" disabled label="Placemennt" placeholder="Enter run time to get placement" value={calculatePlacement()} />

      <Input type="text" maxLength={100} onChange={handleCommentChange} label="Note" placeholder="Optional judge or secretary notes" defaultValue={comment} />
    </div>
  );
};

export default AkcAgilityResults;
